import type { RentalManagerEnvironment } from '@zg-rentals/rental-manager-env-base';
// eslint-disable-next-line no-restricted-imports
import { frontEndRequestV2 } from '@zg-rentals/ts-client-fetch';
import {
  RentalManagerUserSupportWebApiHandler_getHelpTopics,
  RentalManagerUserSupportWebApiHandler_submitRequest,
} from '@zg-rentals/ts-rental-manager-api-client';
import type {
  GetHelpTopicListResponseV2Dto,
  PartialMessageTextAndUrlDto,
  RentalManagerUserSupportRequestV1Dto,
} from '@zg-rentals/ts-rental-manager-api-client';

interface GetHelpArticlesParams {
  category?: string;
  topic?: string;
}

export interface GetHelpArticle {
  title: Array<PartialMessageTextAndUrlDto>;
  contents: Array<Array<PartialMessageTextAndUrlDto>> | [];
}

export interface GetHelpArticlesResponse {
  heading?: string;
  helpArticles: Array<GetHelpArticle>;
}

export type FetchGetHelpArticlesArgs = {
  params: GetHelpArticlesParams;
  onSuccess: (res: GetHelpArticlesResponse) => void;
  onError: () => void;
  environment?: RentalManagerEnvironment;
};

export const fetchGetHelpArticles = ({ params, onSuccess, onError, environment }: FetchGetHelpArticlesArgs) => {
  return frontEndRequestV2<GetHelpArticlesParams, never, GetHelpArticlesResponse>({
    getParams: params,
    endpoint: '/api/v1/support/getHelpArticles',
    method: 'GET',
    contextPath: '/rental-manager-api',
    environment,
  })
    .then(onSuccess)
    .catch(onError);
};

export const fetchGetHelpArticlesForLoggedInUsers = ({
  params,
  onSuccess,
  onError,
  environment,
}: FetchGetHelpArticlesArgs) => {
  return frontEndRequestV2<GetHelpArticlesParams, never, GetHelpArticlesResponse>({
    getParams: params,
    endpoint: '/api/v1/users/support/getHelpArticles',
    method: 'GET',
    contextPath: '/rental-manager-api',
    environment,
  })
    .then(onSuccess)
    .catch(onError);
};

export const fetchGetHelpTopics = (
  onSuccess: (res: GetHelpTopicListResponseV2Dto) => void,
  onError: () => void,
  environment?: RentalManagerEnvironment,
) => RentalManagerUserSupportWebApiHandler_getHelpTopics(environment).then(onSuccess).catch(onError);

export interface AddressAutoCompleteProps {
  partialAddress: string;
}

export interface AutoCompleteAddress {
  name: string;
  city: string;
  state: string;
  street: string;
  propertyId: string;
  zip: string;
  streetNumber: string;
}

export interface AddressAutoCompleteResponse {
  addresses: Array<AutoCompleteAddress>;
}

export const getAddressAutocompleteOptions = (
  params: AddressAutoCompleteProps,
  onSuccess: (res: AddressAutoCompleteResponse) => void,
  onError: () => void,
  environment?: RentalManagerEnvironment,
) => {
  return frontEndRequestV2<AddressAutoCompleteProps, never, AddressAutoCompleteResponse>({
    getParams: params,
    endpoint: `/api/v1/properties/addresses/autocomplete`,
    method: 'GET',
    contextPath: '/rental-manager-api',
    environment,
  })
    .then(onSuccess)
    .catch(onError);
};

export type SupportRequestV2Dto = {
  category: string;
  topic: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  listingAddress?: string;
  unit?: string;
  description: string;
  listingAlias?: string;
};

export const submitUserRequestV2 = (
  params: SupportRequestV2Dto,
  onSuccess: () => void,
  onError: (err: { message: string }) => void,
  environment?: RentalManagerEnvironment,
) => {
  return frontEndRequestV2<SupportRequestV2Dto, never, string>({
    // @ts-ignore
    postData: params,
    endpoint: `/api/v2/users/support/submitRequest`,
    method: 'POST',
    contextPath: '/rental-manager-api',
    environment,
  })
    .then(onSuccess)
    .catch(onError);
};

export const submitUserRequest = (
  params: RentalManagerUserSupportRequestV1Dto,
  onSuccess: () => void,
  onError: (err: { message: string }) => void,
  environment?: RentalManagerEnvironment,
) => RentalManagerUserSupportWebApiHandler_submitRequest(params, environment).then(onSuccess).catch(onError);

export const submitGuestRequestV2 = (
  params: SupportRequestV2Dto,
  onSuccess: () => void,
  onError: (err: { message: string }) => void,
  environment?: RentalManagerEnvironment,
) => {
  return frontEndRequestV2<RentalManagerUserSupportRequestV1Dto, never, string>({
    // @ts-ignore
    postData: params,
    endpoint: `/api/v2/support/submitRequest`,
    method: 'POST',
    contextPath: '/rental-manager-api',
    environment,
  })
    .then(onSuccess)
    .catch(onError);
};

export const submitGuestRequest = (
  params: RentalManagerUserSupportRequestV1Dto,
  onSuccess: () => void,
  onError: (err: { message: string }) => void,
  environment?: RentalManagerEnvironment,
) => {
  return frontEndRequestV2<RentalManagerUserSupportRequestV1Dto, never, string>({
    // @ts-ignore
    postData: params,
    endpoint: `/api/v1/support/submitRequest`,
    method: 'POST',
    contextPath: '/rental-manager-api',
    environment,
  })
    .then(onSuccess)
    .catch(onError);
};

export const fetchGetGuestHelpTopics = (
  onSuccess: (res: GetHelpTopicListResponseV2Dto) => void,
  onError: () => void,
  environment?: RentalManagerEnvironment,
) => {
  return frontEndRequestV2<never, never, GetHelpTopicListResponseV2Dto>({
    endpoint: `/api/v2/support/getHelpTopics`,
    method: 'GET',
    contextPath: '/rental-manager-api',
    environment,
  })
    .then(onSuccess)
    .catch(onError);
};
